import { AnimationEvent } from '@angular/animations'
import { AsyncPipe, NgTemplateOutlet, UpperCasePipe } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { first, fromEvent, map, Observable, Subject, takeUntil, throttleTime } from 'rxjs'

import { FOOTER_DATA } from 'app/core/cms/footer-data.token'
import { NavigationParentItem } from 'app/core/cms/generated/api-types'
import { HEADER_DATA } from 'app/core/cms/header-data.token'
import { CURRENT_LANG } from 'app/core/i18n/language.token'
import { MetaService } from 'app/core/meta/meta.service'
import { extendLangItems, ExtLangItem } from 'app/utils/extend-lang-items.function'
import { onDestroy } from 'app/utils/rxjs/on-destroy.observable'

import { CmsIdDirective } from '../../cms-id.directive'
import { CmsLinkDirective } from '../../cms-link/cms-link.directive'
import { CmsLinkActiveDirective } from '../../cms-link/cms-link-active.directive'
import { IconButtonComponent } from '../../icon-button/icon-button.component'
import { RippleDirective } from '../../ripple/ripple.directive'
import { SwitchingChevronComponent } from '../../switching-chevron/switching-chevron.component'
import { SLIDE_TRIGGER, SUB_LIST_ITEM_TRIGGER, SUB_LIST_TRIGGER } from './side-nav.animations'

export type SideNavAnimationState = 'void' | 'enter'

@Component({
  selector: 'pai-mobile-nav',
  templateUrl: './mobile-nav.component.html',
  styleUrls: ['./mobile-nav.component.scss'],
  imports: [
    NgTemplateOutlet,
    UpperCasePipe,
    SwitchingChevronComponent,
    RippleDirective,
    CmsLinkActiveDirective,
    IconButtonComponent,
    AsyncPipe,
    CmsIdDirective,
    CmsLinkDirective,
  ],
  animations: [SLIDE_TRIGGER, SUB_LIST_TRIGGER, SUB_LIST_ITEM_TRIGGER],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[@slide]': 'animationState',
    '(@slide.done)': 'onAnimationDone($event)',
  },
})
export class MobileNavComponent implements AfterViewInit, OnDestroy {
  readonly afterAnimationStateChanged$: Observable<SideNavAnimationState>

  protected readonly header = inject(HEADER_DATA)
  protected readonly footer = inject(FOOTER_DATA)
  protected readonly languages$: Observable<ExtLangItem[] | null>
  protected readonly element: HTMLElement = inject(ElementRef).nativeElement
  protected animationState: 'void' | 'shown' = 'shown'

  private readonly subNavState = new Map<NavigationParentItem, boolean>()
  private readonly afterAnimationStateChangedSubject = new Subject<SideNavAnimationState>()
  private readonly onDestroy$ = onDestroy()
  private readonly cd = inject(ChangeDetectorRef)

  @ViewChild('scrollContainerRef') private scrollContainerRef: ElementRef

  constructor() {
    this.languages$ = inject(MetaService).altUrls$.pipe(
      map(extendLangItems.bind(void 0, this.footer.languages, inject(CURRENT_LANG))),
    )
    this.afterAnimationStateChanged$ = this.afterAnimationStateChangedSubject.asObservable()
  }

  ngAfterViewInit() {
    fromEvent(this.scrollContainerRef.nativeElement, 'scroll')
      .pipe(throttleTime(250, undefined, { leading: true, trailing: true }), takeUntil(this.onDestroy$))
      .subscribe(this.onScroll.bind(this))
    this.afterAnimationStateChanged$.pipe(first()).subscribe(this.onScroll.bind(this))
  }

  ngOnDestroy(): void {
    this.afterAnimationStateChangedSubject.complete()
  }

  close() {
    this.animationState = 'void'
    this.cd.detectChanges()
  }

  protected onAnimationDone(event: AnimationEvent) {
    this.afterAnimationStateChangedSubject.next(<SideNavAnimationState>event.toState)
  }

  protected toggleSubNav(navParentItem: NavigationParentItem) {
    this.subNavState.set(navParentItem, !(this.subNavState.get(navParentItem) || false))
  }

  protected closeSubNav(navParentItem: NavigationParentItem) {
    this.subNavState.set(navParentItem, false)
  }

  protected isOpen(navParentItem: NavigationParentItem): boolean {
    return this.subNavState.get(navParentItem) || false
  }

  private onScroll() {
    const el = this.scrollContainerRef.nativeElement
    const showBefore = el.scrollTop > 0
    const showAfter = el.scrollTop + el.clientHeight < el.scrollHeight
    this.element.classList.toggle('--scroll-hint-before', showBefore)
    this.element.classList.toggle('--scroll-hint-after', showAfter)
  }
}
